import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useCart } from '../../contexts/CartContext';
import { useShippingSettings } from '../../hooks/useShippingSettings';
import { 
  ShoppingBagIcon,
  MapPinIcon,
  CreditCardIcon,
  ClockIcon,
  BuildingOfficeIcon,
  DocumentTextIcon,
  BoltIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon
} from '@heroicons/react/24/outline';
import axios from '../../utils/axios';
import toast from 'react-hot-toast';
import AddressSelector from '../../components/address/AddressSelector';
import FormInput from '../../components/ui/FormInput';
import AccessibleButton from '../../components/ui/AccessibleButton';
import LoadingSpinner from '../../components/ui/LoadingSpinner';

const B2BFastCheckout = () => {
  const { user } = useAuth();
  const { cartItems, getTotalPrice, getTotal, getCartSummary, clearCart } = useCart();
  const { calculateShipping, calculateTax, settings } = useShippingSettings();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  // Company and address data
  const [companyData, setCompanyData] = useState(null);
  const [savedAddresses, setSavedAddresses] = useState([]);
  const [selectedAddress, setSelectedAddress] = useState(null);
  const [useCompanyAddress, setUseCompanyAddress] = useState(true);
  
  // Order details
  const paymentMethod = 'company_credit'; // Default payment method - no selection needed
  const [orderNotes, setOrderNotes] = useState('');
  const [purchaseOrder, setPurchaseOrder] = useState('');
  
  // Fast checkout options
  const [saveAsTemplate, setSaveAsTemplate] = useState(false);
  const [templateName, setTemplateName] = useState('');

  useEffect(() => {
    if (!user || user.role !== 'b2b') {
      navigate('/login');
      return;
    }
    
    if (!cartItems || cartItems.length === 0) {
      toast.error('העגלה ריקה');
      navigate('/trade');
      return;
    }
    
    loadCheckoutData();
  }, [user, cartItems, navigate]);

  const loadCheckoutData = async () => {
    try {
      setLoading(true);
      
      // Use company data from user object instead of API call
      if (user.company) {
        setCompanyData(user.company);
      }
      
      // Load saved addresses
      const addressResponse = await axios.get('/api/addresses');
      setSavedAddresses(addressResponse.data || []);
      
      // Set default address
      const defaultAddress = addressResponse.data?.find(addr => addr.is_default);
      if (defaultAddress) {
        setSelectedAddress(defaultAddress);
        setUseCompanyAddress(false);
      }
      
    } catch (error) {
      console.error('Error loading checkout data:', error);
      toast.error('שגיאה בטעינת נתוני הכתובות');
    } finally {
      setLoading(false);
    }
  };

  const getShippingAddress = () => {
    if (useCompanyAddress && companyData) {
      return {
        first_name: user.first_name,
        last_name: user.last_name,
        company_name: companyData.name,
        address: companyData.address,
        city: companyData.city,
        postal_code: companyData.postal_code,
        phone: companyData.phone || user.phone,
        email: user.email
      };
    } else if (selectedAddress) {
      return {
        first_name: selectedAddress.first_name,
        last_name: selectedAddress.last_name,
        company_name: selectedAddress.company_name,
        address: selectedAddress.address_line_1 + (selectedAddress.address_line_2 ? ', ' + selectedAddress.address_line_2 : ''),
        city: selectedAddress.city,
        postal_code: selectedAddress.postal_code,
        phone: selectedAddress.phone,
        email: selectedAddress.email
      };
    }
    return null;
  };

  const calculateOrderTotals = () => {
    console.log('💰 calculateOrderTotals - cartItems:', cartItems);
    
    // Try multiple methods to get subtotal
    let subtotal = 0;
    try {
      const totalPrice = getTotalPrice ? getTotalPrice() : (getTotal ? getTotal() : 0);
      const summary = getCartSummary ? getCartSummary() : null;
      
      if (summary && summary.subtotal) {
        subtotal = summary.subtotal;
      } else if (totalPrice) {
        subtotal = totalPrice;
      } else if (cartItems && cartItems.length > 0) {
        // Fallback: calculate manually
        subtotal = cartItems.reduce((sum, item) => {
          const product = item.product || item;
          const price = product.price || product.price_b2b || product.price_b2c || 0;
          const quantity = item.quantity || 1;
          return sum + (price * quantity);
        }, 0);
      }
    } catch (error) {
      console.error('Error calculating subtotal:', error);
      subtotal = 0;
    }
    
    const shipping = calculateShipping(subtotal);
    const tax = calculateTax(subtotal);
    const total = subtotal + shipping + tax;
    
    console.log('💰 Totals:', { subtotal, shipping, tax, total });
    
    return { subtotal, shipping, tax, total };
  };

  const handleFastOrder = async () => {
    console.log('🚀 handleFastOrder called');
    console.log('📦 cartItems:', cartItems);
    
    if (!cartItems || cartItems.length === 0) {
      toast.error('העגלה ריקה. אנא הוסף מוצרים לעגלה');
      return;
    }

    const shippingAddress = getShippingAddress();
    if (!shippingAddress) {
      toast.error('אנא בחר כתובת משלוח');
      return;
    }

    // Payment method is always company_credit - no need to check

    setIsSubmitting(true);
    try {
      const { subtotal, shipping, tax, total } = calculateOrderTotals();
      
      // Map cart items to order items format
      const orderItems = cartItems.map(item => {
        // Handle both product and bundle items
        const product = item.product || item;
        const productId = product.id || item.product_id;
        const quantity = item.quantity || 1;
        const unitPrice = product.price || product.price_b2b || product.price_b2c || 0;
        
        console.log('📋 Mapping item:', { productId, quantity, unitPrice, product });
        
        if (!productId) {
          console.error('❌ Missing product ID for item:', item);
          throw new Error('חסר מזהה מוצר');
        }
        
        return {
          product_id: productId,
          quantity: quantity,
          unit_price: unitPrice
        };
      });
      
      // Build order data - use shipping_address_id if selected address exists
      // Otherwise use customer_details (for company address or new address)
      const orderData = {
        items: orderItems,
        subtotal,
        shipping_cost: shipping,
        tax_amount: tax,
        total_amount: total,
        payment_method: paymentMethod,
        order_type: 'b2b',
        notes: orderNotes,
        purchase_order_number: purchaseOrder,
        fast_checkout: true
      };

      // If using a saved address, send shipping_address_id
      // Otherwise, send customer_details (for company address)
      if (selectedAddress && !useCompanyAddress) {
        // Using a saved address - send the ID
        orderData.shipping_address_id = selectedAddress.id;
        console.log('📮 Using saved address ID:', selectedAddress.id);
      } else {
        // Using company address or need to create new - send customer_details
        orderData.customer_details = shippingAddress;
        // Only set create_new_address if user explicitly added a new address
        // (this will be handled by AddressSelector component when saving new address)
        console.log('📮 Using customer_details (company address or new)');
      }

      console.log('📤 Sending order data:', orderData);
      const response = await axios.post('/api/orders', orderData);
      console.log('✅ Order response:', response.data);
      
      if (response.data.order) {
        const order = response.data.order;
        const orderId = order.id || order.order_id;
        
        console.log('📦 STEP 1: Full order response:', response.data);
        console.log('✅ STEP 2: Order created successfully');
        console.log('📍 Order object:', order);
        console.log('📍 Order ID:', orderId);
        console.log('📍 Order number:', order.order_number);
        
        if (!orderId) {
          console.error('❌ STEP 2.5: No order ID found!');
          toast.error('ההזמנה נוצרה אבל לא התקבל מזהה הזמנה');
          navigate('/account/order-history');
          return;
        }
        
        // Payment will be handled later by admin - no need to reserve credit now
        
        // Save as template if requested
        if (saveAsTemplate && templateName) {
          try {
            console.log('🔄 STEP 3: Saving template');
            await axios.post('/api/order-templates', {
              name: templateName,
              description: `תבנית מהזמנה מהירה - ${new Date().toLocaleDateString('he-IL')}`,
              items: cartItems.map(item => ({
                product_id: item.product.id,
                quantity: item.quantity,
                notes: ''
              }))
            });
            toast.success('התבנית נשמרה בהצלחה');
          } catch (templateError) {
            console.error('Error saving template:', templateError);
          }
        }
        
        console.log('🔄 STEP 4: Showing success message');
        toast.success('ההזמנה נוצרה בהצלחה! מעביר לפרטי ההזמנה...', {
          duration: 2000
        });
        
        console.log('🔄 STEP 5: Clearing cart');
        clearCart().catch(err => console.error('Error clearing cart:', err));
        
        console.log('🔄 STEP 6: Preparing navigation');
        const orderUrl = `/orders/${orderId}`;
        
        console.log('🚀 NAVIGATION START:');
        console.log('   - Order ID:', orderId);
        console.log('   - Order URL:', orderUrl);
        console.log('   - Current location:', window.location.href);
        console.log('   - Order object keys:', Object.keys(order));
        
        console.log('🔄 STEP 7: Executing navigation');
        if (orderId) {
          console.log('✅ About to navigate to:', orderUrl);
          console.log('✅ Order ID is valid:', orderId);
          
          // Use navigate from react-router with replace to prevent back button issues
          // Wait a bit to ensure toast is shown and cart is cleared
          setTimeout(() => {
            console.log('🔄 Executing navigation now...');
            navigate(orderUrl, { replace: true });
          }, 300);
          console.log('✅ Navigation scheduled - should navigate in 300ms');
        } else {
          console.error('❌ Cannot navigate - no order ID!');
          navigate('/orders', { replace: true });
        }
      } else {
        console.error('❌ No order in response!');
        toast.error('ההזמנה נוצרה אבל לא התקבלו פרטים. אנא בדוק את ההזמנות שלך.');
        navigate('/account/order-history');
      }
    } catch (error) {
      console.error('❌ Error creating order:', error);
      console.error('Error details:', error.response?.data || error.message);
      
      // Show more detailed error message
      const errorMessage = error.response?.data?.message || 
                          error.response?.data?.error ||
                          error.message ||
                          'שגיאה ביצירת ההזמנה. אנא נסה שוב.';
      
      toast.error(errorMessage);
      
      // If it's a validation error, show specific fields
      if (error.response?.data?.errors) {
        const errors = error.response.data.errors;
        Object.keys(errors).forEach(field => {
          toast.error(`${field}: ${errors[field]}`);
        });
      }
    } finally {
      setIsSubmitting(false);
    }
  };


  if (loading) {
    return (
      <LoadingSpinner 
        fullScreen 
        size="xl" 
        color="primary" 
        text="טוען נתוני תשלום מהיר..."
      />
    );
  }

  const { subtotal, shipping, tax, total } = calculateOrderTotals();

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-blue-50/30 to-purple-50/20 pb-safe" dir="rtl">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-6 lg:py-8" role="main" aria-label="תשלום מהיר B2B">
        {/* Header - Enhanced Design */}
        <header className="mb-6 sm:mb-8" role="banner">
          <div className="bg-gradient-to-r from-blue-600 to-purple-600 rounded-xl shadow-lg p-6 sm:p-8 mb-6">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <div className="flex items-center gap-3 sm:gap-4">
                <div className="bg-white/20 backdrop-blur-sm rounded-xl p-3 sm:p-4" aria-hidden="true">
                  <BoltIcon className="h-8 w-8 sm:h-10 sm:w-10 text-yellow-300" />
                </div>
                <div>
                  <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-white flex items-center gap-2">
                    תשלום מהיר B2B
                  </h1>
                  <p className="text-sm sm:text-base text-blue-100 mt-1 sm:mt-2">
                    תהליך תשלום מהיר ויעיל עבור לקוחות עסקיים
                  </p>
                </div>
              </div>
              <div className="bg-white/10 backdrop-blur-sm rounded-lg p-4 border border-white/20">
                <p className="text-xs sm:text-sm text-blue-200 mb-1">חברה</p>
                <p className="text-base sm:text-lg font-bold text-white" aria-label={`חברה: ${companyData?.name || 'לא זמין'}`}>
                  {companyData?.name || 'לא זמין'}
                </p>
              </div>
            </div>
          </div>
        </header>

        <div className="flex flex-col lg:grid lg:grid-cols-3 gap-4 sm:gap-6 lg:gap-8">
          {/* Order Summary - Enhanced Design */}
          <aside className="lg:col-span-1 lg:order-last order-first" aria-label="סיכום הזמנה">
            <div className="bg-white rounded-xl shadow-xl border border-gray-100 p-5 sm:p-6 lg:sticky lg:top-8 transition-all hover:shadow-2xl">
              <div className="flex items-center gap-3 mb-5 pb-4 border-b border-gray-200">
                <div className="bg-gradient-to-br from-blue-500 to-purple-600 rounded-lg p-2" aria-hidden="true">
                  <ShoppingBagIcon className="h-5 w-5 text-white" />
                </div>
                <h2 className="text-lg sm:text-xl font-bold text-gray-900">סיכום הזמנה</h2>
                <span className="mr-auto bg-blue-100 text-blue-700 text-xs font-bold px-2.5 py-1 rounded-full" aria-label={`${cartItems.length} פריטים בעגלה`}>
                  {cartItems.length} פריטים
                </span>
              </div>
              
              {/* Cart Items - Enhanced */}
              <div className="space-y-3 mb-5 max-h-64 overflow-y-auto custom-scrollbar pr-1">
                {cartItems.map((item, index) => {
                  const product = item.product || item;
                  const itemTotal = (parseFloat(product.price || product.price_b2b || product.price_b2c) * (item.quantity || 1)) || 0;
                  const imageUrl = product.images?.[0] 
                    ? (product.images[0].startsWith('http') ? product.images[0] : `/api${product.images[0]}`)
                    : 'https://via.placeholder.com/80';
                  
                  return (
                    <div 
                      key={item.id || index} 
                      className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg hover:bg-gray-100 hover:shadow-md transition-all duration-200 border border-gray-100 group touch-manipulation"
                      role="listitem"
                      aria-label={`${product.name}, כמות: ${item.quantity}, מחיר: ₪${itemTotal.toFixed(2)}`}
                    >
                      <div className="flex-shrink-0">
                        <img 
                          src={imageUrl} 
                          alt={product.name || 'תמונת מוצר'}
                          className="w-14 h-14 sm:w-16 sm:h-16 rounded-lg object-cover shadow-sm border-2 border-white group-hover:scale-105 transition-transform duration-200"
                          loading="lazy"
                        />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-semibold text-sm text-gray-900 line-clamp-2 mb-1">{product.name}</p>
                        <div className="flex items-center gap-2 flex-wrap text-xs text-gray-600">
                          <span className="bg-blue-100 text-blue-700 px-2 py-0.5 rounded font-medium whitespace-nowrap">
                            כמות: {item.quantity}
                          </span>
                          {product.sku && (
                            <span className="text-gray-500 font-mono text-[10px] sm:text-xs truncate max-w-[80px] sm:max-w-none" title={product.sku}>
                              {product.sku}
                            </span>
                          )}
                        </div>
                      </div>
                      <div className="flex-shrink-0 text-left">
                        <p className="font-bold text-base text-gray-900">
                          ₪{itemTotal.toFixed(2)}
                        </p>
                        <p className="text-xs text-gray-500">
                          ₪{(itemTotal / (item.quantity || 1)).toFixed(2)} ליח'
                        </p>
                      </div>
                    </div>
                  );
                })}
              </div>
              
              {/* Totals - Enhanced */}
              <div className="border-t-2 border-gray-200 pt-4 space-y-2.5 mb-5">
                <div className="flex justify-between items-center text-sm">
                  <span className="text-gray-600 flex items-center gap-2">
                    <span className="w-2 h-2 bg-gray-300 rounded-full"></span>
                    סכום ביניים
                  </span>
                  <span className="font-semibold text-gray-900">₪{(parseFloat(subtotal) || 0).toFixed(2)}</span>
                </div>
                
                <div className="flex justify-between items-center text-sm">
                  <span className="text-gray-600 flex items-center gap-2">
                    <span className="w-2 h-2 bg-gray-300 rounded-full"></span>
                    משלוח
                  </span>
                  <span className="font-semibold">
                    {shipping === 0 ? (
                      <span className="text-green-600 flex items-center gap-1">
                        <span className="text-xs">✓</span>
                        חינם
                      </span>
                    ) : (
                      `₪${(parseFloat(shipping) || 0).toFixed(2)}`
                    )}
                  </span>
                </div>
                
                <div className="flex justify-between items-center text-sm">
                  <span className="text-gray-600 flex items-center gap-2">
                    <span className="w-2 h-2 bg-gray-300 rounded-full"></span>
                    מע"מ
                  </span>
                  <span className="font-semibold text-gray-900">₪{(parseFloat(tax) || 0).toFixed(2)}</span>
                </div>
                
                <div className="border-t-2 border-blue-200 pt-3 mt-3 bg-gradient-to-r from-blue-50 to-purple-50 -mx-3 px-3 py-2 rounded-lg">
                  <div className="flex justify-between items-center">
                    <span className="text-base sm:text-lg font-bold text-gray-900">סה"כ לתשלום</span>
                    <span className="text-xl sm:text-2xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
                      ₪{(parseFloat(total) || 0).toFixed(2)}
                    </span>
                  </div>
                </div>
              </div>
              
              {/* Action Buttons - Enhanced */}
              <div className="space-y-3">
                <AccessibleButton
                  type="button"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    handleFastOrder();
                  }}
                  disabled={
                    isSubmitting || 
                    !getShippingAddress() || 
                    !cartItems ||
                    cartItems.length === 0
                  }
                  loading={isSubmitting}
                  fullWidth
                  size="lg"
                  variant="primary"
                  icon={BoltIcon}
                  iconPosition="left"
                  ariaLabel="הזמן מהר - יצירת הזמנה"
                  className="relative overflow-hidden group bg-gradient-to-r from-blue-600 via-purple-600 to-blue-600 hover:from-blue-700 hover:via-purple-700 hover:to-blue-700 shadow-lg hover:shadow-xl"
                >
                  {!isSubmitting && (
                    <>
                      <span>הזמן מהר</span>
                      <span className="text-sm opacity-90 mr-1">→</span>
                    </>
                  )}
                  {isSubmitting && <span>מעבד הזמנה...</span>}
                </AccessibleButton>
                
                {(!getShippingAddress() || !cartItems || cartItems.length === 0) && (
                  <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-center" role="alert">
                    <div className="flex items-center justify-center gap-2 text-xs text-red-700 font-medium">
                      {(!cartItems || cartItems.length === 0) && (
                        <>
                          <ExclamationTriangleIcon className="h-4 w-4 flex-shrink-0" />
                          <span>העגלה ריקה</span>
                        </>
                      )}
                      {(!cartItems || cartItems.length === 0) && !getShippingAddress() && <span>•</span>}
                      {!getShippingAddress() && (
                        <>
                          <MapPinIcon className="h-4 w-4 flex-shrink-0" />
                          <span>בחר כתובת משלוח</span>
                        </>
                      )}
                    </div>
                  </div>
                )}
                
                <div className="flex items-center gap-2 justify-center pt-2 px-2">
                  <CreditCardIcon className="h-4 w-4 text-gray-400 flex-shrink-0" aria-hidden="true" />
                  <p className="text-xs text-gray-500 text-center">
                    תשלום מהיר מיועד ללקוחות עסקיים מאושרים
                  </p>
                </div>
              </div>
            </div>
          </aside>

          {/* Main Content - Enhanced */}
          <div className="lg:col-span-2 space-y-5 sm:space-y-6 order-last lg:order-first">
            {/* Shipping Address - Enhanced */}
            <div className="bg-white rounded-xl shadow-lg border border-gray-100 p-5 sm:p-6 transition-all hover:shadow-xl">
              <div className="flex items-center gap-3 mb-5 pb-4 border-b border-gray-200">
                <div className="bg-gradient-to-br from-green-500 to-emerald-600 rounded-lg p-2">
                  <MapPinIcon className="h-5 w-5 text-white" />
                </div>
                <h3 className="text-lg sm:text-xl font-bold text-gray-900">כתובת משלוח</h3>
              </div>
              
              <AddressSelector
                selectedAddress={selectedAddress}
                onAddressSelect={setSelectedAddress}
                companyData={companyData}
                useCompanyAddress={useCompanyAddress}
                onUseCompanyAddressChange={setUseCompanyAddress}
              />
            </div>

            {/* Order Details - Enhanced */}
            <div className="bg-white rounded-xl shadow-lg border border-gray-100 p-5 sm:p-6 transition-all hover:shadow-xl">
              <div className="flex items-center gap-3 mb-5 pb-4 border-b border-gray-200">
                <div className="bg-gradient-to-br from-purple-500 to-pink-600 rounded-lg p-2">
                  <DocumentTextIcon className="h-5 w-5 text-white" />
                </div>
                <h3 className="text-lg sm:text-xl font-bold text-gray-900">פרטי הזמנה</h3>
              </div>
              
              <div className="space-y-5">
                <FormInput
                  label="מספר הזמנת רכש (אופציונלי)"
                  type="text"
                  name="purchaseOrder"
                  value={purchaseOrder}
                  onChange={(e) => setPurchaseOrder(e.target.value)}
                  placeholder="PO-2024-001"
                  helpText="מספר הזמנת רכש מהחברה שלך (למעקב פנימי)"
                  className="text-base"
                />
                
                <div>
                  <label 
                    htmlFor="orderNotes"
                    className="block text-sm font-semibold text-gray-700 mb-2 flex items-center gap-2"
                  >
                    <DocumentTextIcon className="h-4 w-4 text-gray-400" />
                    הערות להזמנה
                  </label>
                  <textarea
                    id="orderNotes"
                    name="orderNotes"
                    value={orderNotes}
                    onChange={(e) => {
                      const value = e.target.value;
                      if (value.length <= 500) {
                        setOrderNotes(value);
                      }
                    }}
                    rows={4}
                    maxLength={500}
                    className="w-full px-4 py-3 text-sm sm:text-base border-2 border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all hover:border-gray-300 resize-none"
                    placeholder="הערות מיוחדות, הוראות אספקה, בקשות מיוחדות וכו'"
                    aria-describedby="orderNotes-help orderNotes-count"
                  />
                  <div className="flex items-center justify-between mt-1.5">
                    <p 
                      id="orderNotes-help"
                      className="text-xs text-gray-500"
                    >
                      הערות אלה יועברו למחלקת ההזמנות
                    </p>
                    <p 
                      id="orderNotes-count"
                      className={`text-xs font-medium ${
                        orderNotes.length > 450 
                          ? 'text-orange-600' 
                          : orderNotes.length > 400 
                            ? 'text-yellow-600' 
                            : 'text-gray-500'
                      }`}
                    >
                      {orderNotes.length}/500
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Save as Template Option - Enhanced */}
            <div className="bg-white rounded-xl shadow-lg border border-gray-100 p-5 sm:p-6 transition-all hover:shadow-xl">
              <div className="flex items-center gap-3 mb-5 pb-4 border-b border-gray-200">
                <div className="bg-gradient-to-br from-yellow-500 to-orange-600 rounded-lg p-2">
                  <ClockIcon className="h-5 w-5 text-white" />
                </div>
                <h3 className="text-lg sm:text-xl font-bold text-gray-900">אפשרויות נוספות</h3>
              </div>
              
              <div className="space-y-4">
                <label className="flex items-start gap-3 cursor-pointer touch-manipulation group p-3 rounded-lg hover:bg-gray-50 transition-colors focus-within:ring-2 focus-within:ring-blue-500 focus-within:ring-offset-2 rounded-lg">
                  <input
                    type="checkbox"
                    checked={saveAsTemplate}
                    onChange={(e) => setSaveAsTemplate(e.target.checked)}
                    className="mt-1 h-5 w-5 min-h-[44px] min-w-[44px] text-blue-600 rounded border-gray-300 focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 cursor-pointer"
                    aria-describedby="template-help"
                  />
                  <div className="flex-1 pt-1">
                    <span className="text-sm sm:text-base font-medium text-gray-900 block">שמור כתבנית הזמנה לעתיד</span>
                    <span id="template-help" className="text-xs text-gray-500 mt-1 block">תוכל להשתמש בתבנית זו להזמנות חוזרות</span>
                  </div>
                </label>
                
                {saveAsTemplate && (
                  <div className="mr-7 bg-blue-50 border border-blue-200 rounded-lg p-4 transition-all duration-300 ease-out">
                    <FormInput
                      label="שם התבנית"
                      type="text"
                      name="templateName"
                      value={templateName}
                      onChange={(e) => setTemplateName(e.target.value)}
                      placeholder="לדוגמה: הזמנה חודשית רגילה"
                      required={saveAsTemplate}
                      helpText="שם התבנית יעזור לך לזהות אותה בעתיד"
                      maxLength={50}
                      className="bg-white"
                    />
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default B2BFastCheckout;
